/**
 * Leitura e escrita de grupos de bits de largura fixa.
 *
 * Os codecs de texto sao numeracoes posicionais: 10 bits por emoji
 * (base-1024), 12 bits por palavra (base-4096). O comprimento vai num
 * prefixo de 4 bytes, senao o preenchimento final do ultimo grupo seria
 * indistinguivel de dados — 4 bytes de payload, por exemplo, ocupam 40 bits
 * em emoji e sairiam como 5 bytes na volta.
 */
import { readU32BE, u32be, view } from './binary.js'
import { DisguiseError } from './types.js'

export const LENGTH_PREFIX_BYTES = 4

export function withLengthPrefix(payload: Uint8Array): Uint8Array {
  const out = new Uint8Array(LENGTH_PREFIX_BYTES + payload.length)
  out.set(u32be(payload.length), 0)
  out.set(payload, LENGTH_PREFIX_BYTES)
  return out
}

export function stripLengthPrefix(bytes: Uint8Array): Uint8Array {
  if (bytes.length < LENGTH_PREFIX_BYTES) throw new DisguiseError('payload sem prefixo de tamanho')
  const declared = readU32BE(bytes, 0)
  if (declared > bytes.length - LENGTH_PREFIX_BYTES) {
    throw new DisguiseError('tamanho declarado maior do que o conteudo recebido')
  }
  return bytes.slice(LENGTH_PREFIX_BYTES, LENGTH_PREFIX_BYTES + declared)
}

/** Bytes → indices de `width` bits. O ultimo grupo e completado com zeros. */
export function toSymbols(bytes: Uint8Array, width: number): number[] {
  const symbols: number[] = []
  let acc = 0
  let bits = 0
  for (const byte of bytes) {
    acc = (acc << 8) | byte
    bits += 8
    while (bits >= width) {
      bits -= width
      symbols.push((acc >>> bits) & ((1 << width) - 1))
      acc &= (1 << bits) - 1
    }
  }
  if (bits > 0) symbols.push((acc << (width - bits)) & ((1 << width) - 1))
  return symbols
}

/** Indices de `width` bits → bytes. Bits sobrando no fim sao preenchimento. */
export function fromSymbols(symbols: readonly number[], width: number): Uint8Array {
  const out = new Uint8Array(Math.floor((symbols.length * width) / 8))
  let acc = 0
  let bits = 0
  let pos = 0
  for (const symbol of symbols) {
    if (symbol < 0 || symbol >= 1 << width) {
      throw new DisguiseError(`simbolo ${symbol} fora da base de ${width} bits`)
    }
    acc = (acc << width) | symbol
    bits += width
    while (bits >= 8) {
      bits -= 8
      out[pos++] = (acc >>> bits) & 0xff
      acc &= (1 << bits) - 1
    }
  }
  return out.subarray(0, pos)
}

/** Bytes → bits soltos, do mais significativo para o menos. Usado no LSB do PNG. */
export function toBits(bytes: Uint8Array): Uint8Array {
  const dv = view(bytes)
  const bits = new Uint8Array(bytes.length * 8)
  for (let i = 0; i < bytes.length; i++) {
    const byte = dv.getUint8(i)
    for (let b = 0; b < 8; b++) bits[i * 8 + b] = (byte >> (7 - b)) & 1
  }
  return bits
}

export function fromBits(bits: Uint8Array): Uint8Array {
  const out = new Uint8Array(Math.floor(bits.length / 8))
  for (let i = 0; i < out.length; i++) {
    let byte = 0
    for (let b = 0; b < 8; b++) byte = (byte << 1) | (bits[i * 8 + b] ?? 0)
    out[i] = byte
  }
  return out
}
